import { useState, useEffect } from 'react';
import {
  getConsumerCheckInCount,
  requestCheckin,
} from '../../../services/NetworkServices';
import { Box, Typography, Modal } from '@mui/material';
import StyledButton from '../../../components/Button/Button';
import DownloadIcon from '../../../assets/Login new.png';
import FilterIcon from '../../../assets/Filter new.png';
import FilterComponent from '../../../components/FilterComponent/FilterComponent';
import { useParams, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { BabysitterContext } from '../../../context/BabysitterContext';
import { BabysitterContextProps } from '../../../context/BabysitterContext';
import CallIcon from '../../../assets/call icon new.png';
import MailIcon from '../../../assets/mail icon new.png';
import TopNav from '../../../components/TopNav/TopNav';
import Profile from './Profile';
import Popup from '../../../components/Inputs/Popup';
import { AxiosError } from 'axios';
import { formatTime } from '../../../utils';
import alert_icon from '../../../assets/BabySitterDashboard/alert-icon.svg';
import PopupClose from '../../../assets/popupClose.svg';

interface CheckInProps {
  showProfileFace: boolean;
  setShowProfileFace: React.Dispatch<React.SetStateAction<boolean>>;
}

const ConsumerCheckInCounts: React.FC<CheckInProps> = ({
  showProfileFace,
  setShowProfileFace,
}) => {
  const [profileDetails, setProfileDetails] = useState<any>({});
  const [checkInCount, setCheckInCount] = useState<number>(0);
  const [showFilter, setShowFilter] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [showScannedFace, setShowScannedFace] = useState<boolean>(false);
  const [scannedFaceDoc, setShowScannedFaceDoc] = useState<string>('');
  const [requestCheck, setRequestCheck] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [popupMessage, setPopupMessage] = useState<string>('');
  const [showConfirmationPopup, setShowConfirmationPopup] =
    useState<boolean>(false);

  const context = useContext(BabysitterContext) as BabysitterContextProps;
  if (!context) {
    throw new Error(
      'Babysitter Context must be used within a BabysitterProvider',
    );
  }
  const {
    selectedStartDate,
    selectedEndDate,
    payload,
    handlePayloadChange,
    formatDate,
    checkInsList,
    handleShowDatePicker,
    showDatePickerForDownload,
    handleShowDatePickerForDownload,
  } = context;

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const onClickDownloadIcon = () => {
    handleShowDatePickerForDownload(true);
  };

  const handleFilterChange = (value: string) => {
    let newStartDate = new Date();
    let newEndDate = new Date();

    switch (value) {
      case 'thisMonth':
        newStartDate.setDate(1);
        break;
      case 'this3Months':
        newStartDate.setMonth(newStartDate.getMonth() - 3);
        break;
      case 'this6Months':
        newStartDate.setMonth(newStartDate.getMonth() - 6);
        break;
      case 'thisYear':
        newStartDate.setFullYear(newStartDate.getFullYear() - 1);
        break;
      case 'selectPeriod':
        handleShowDatePicker(true);
        setShowFilter(false); // Show date picker modal
        return;
      default:
        return;
    }

    let newFilterValue = {
      startDate: formatDate(newStartDate),
      endDate: formatDate(newEndDate),
    };

    handlePayloadChange({ ...newFilterValue });
    setShowFilter(false); // Close modal after selection
  };

  const getConsumerCheckInCounts = async () => {
    if (id) {
      try {
        console.log('payload is', payload);
        const response = await getConsumerCheckInCount(id, payload);
        const data = response.data;
        setProfileDetails(data.data.profileDetails);
        setCheckInCount(data.data.consumerCheckInCount);
        console.log('data is', data.data.consumerCheckInCount);
      } catch (error) {
        console.error('Error fetching check-in count:', error);
      }
    } else {
      console.error('ID is undefined');
    }
  };

  const handleProfile = (value: string) => {
    setShowProfileFace(true);
    setShowScannedFaceDoc(value);
  };

  const backClick = () => {
    setShowProfileFace(false);
  };

  const handleRequestCheckins = async (value: string) => {
    const currentTime = new Date();
    const expiryTime = new Date(currentTime.getTime() + 60 * 60 * 1000);

    const checkInInitiatedTime = formatTime(currentTime);
    const checkInInitiatedExpiryTime = formatTime(expiryTime);
    const checkInInitiatedDate = currentTime.toISOString().split('T')[0];

    const requestData = {
      consumerId: value,
      checkIn_initiated_time: checkInInitiatedTime,
      checkIn_initiated_expiry_time: checkInInitiatedExpiryTime,
      checkIn_initiated_date: checkInInitiatedDate,
    };

    try {
      const response = await requestCheckin(requestData);
      const data = await response?.data;
      if (data) {
        setRequestCheck(true);
        setPopupMessage(data?.message);
        setShowConfirmationPopup(false);
        setShowPopup(true);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        setPopupMessage(error?.response?.data?.message);
        setShowConfirmationPopup(false);
        setShowPopup(true);
      }
    }
  };

  const handleClickYes = () => {
    setShowConfirmationPopup(false);
    handleRequestCheckins(id || '');
  };

  const handleClickNo = () => {
    setShowConfirmationPopup(false);
  };

  const handlePopup = () => {
    setShowPopup(false);
    setShowConfirmationPopup(false);
    setRequestCheck(false);
  };

  useEffect(() => {
    getConsumerCheckInCounts();
  }, [payload]);

  if (showProfileFace) {
    return (
      <Profile
        showScannedFace={showProfileFace}
        scannedFaceDoc={scannedFaceDoc}
        backClick={backClick}
        id={id}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderColor: '#365F91',
          border: '1.4px solid transparent',
          background: `
                  linear-gradient(120deg, black -4.29%, #25313c 100%) padding-box,
                  linear-gradient(180deg, #365f91 -4.29%, rgba(54, 95, 145, 0) 100%) border-box
                `,
          borderImageSlice: 1,
          borderRadius: '20px',
          backdropFilter: 'blur(117.99954986572266px)',
          mx: { xs: 2, sm: 10 },
          my: { xs: 2, sm: 5 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'column', md: 'row' },
            justifyContent: 'space-between',
            padding: { xs: '10px', sm: '30px' },
          }}
        >
          <Box gap={2} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              gap={1}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <img
                src={profileDetails.profilePic}
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  border: '4px solid white',
                }}
                onClick={() => handleProfile(profileDetails.profilePic)}
              />
              <Box gap={1} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Opensans',
                    fontWeight: '700',
                    textAlign: 'left',
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                  }}
                >
                  {profileDetails.firstName} {profileDetails.lastName}
                </Typography>
                {/* Desktop View */}
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Box
                    gap={2}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'column', md: 'row' },
                    }}
                  >
                    <Box gap={1} sx={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src={CallIcon}
                        alt="Call"
                        style={{
                          width: '25px',
                          height: '25px',
                          marginRight: '5px',
                        }}
                      />
                      <Typography
                        sx={{
                          whiteSpace: 'normal',
                          overflowWrap: 'break-word',
                          wordBreak: 'break-all',
                        }}
                      >
                        {profileDetails.countryCode} {profileDetails.phone}
                      </Typography>
                    </Box>
                    <Box gap={1} sx={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src={MailIcon}
                        alt="Mail"
                        style={{
                          width: '25px',
                          height: '25px',
                          marginRight: '5px',
                        }}
                      />
                      <Typography
                        sx={{
                          whiteSpace: 'normal',
                          overflowWrap: 'break-word',
                          wordBreak: 'break-all',
                        }}
                      >
                        {profileDetails.userId?.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            {/* Mobile View */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <Box
                gap={2}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'column', md: 'row' },
                }}
              >
                <Box
                  gap={1}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: '1 1 auto',
                  }}
                >
                  <img
                    src={CallIcon}
                    alt="Call"
                    style={{
                      width: '25px',
                      height: '25px',
                      marginRight: '5px',
                    }}
                  />
                  <Typography
                    sx={{
                      whiteSpace: 'normal',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-all',
                    }}
                  >
                    {profileDetails.countryCode} {profileDetails.phone}
                  </Typography>
                </Box>
                <Box
                  gap={1}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: '1 1 auto',
                  }}
                >
                  <img
                    src={MailIcon}
                    alt="Mail"
                    style={{
                      width: '25px',
                      height: '25px',
                      marginRight: '5px',
                    }}
                  />
                  <Typography
                    sx={{
                      whiteSpace: 'normal',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-all',
                    }}
                  >
                    {profileDetails.userId?.email}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Typography sx={{ textAlign: 'left' }}>
              <span style={{ color: 'grey' }}>Total Check-ins: </span>
              {checkInCount}
            </Typography>
          </Box>
          <Box gap={3} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              gap={2}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'start',
                justifyContent: 'flex-end',
              }}
            >
              <StyledButton onClick={() => navigate('/supervisor/group-chat')}>
                Message
              </StyledButton>
              <img
                src={DownloadIcon}
                alt="Download"
                onClick={onClickDownloadIcon}
                style={{ width: '56px', height: '56px' }}
              />
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    width: '56px',
                    height: '56px',
                    backgroundColor: 'black',
                    borderRadius: '50%',
                    padding: '10px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowFilter((prev) => !prev)}
                >
                  <img
                    src={FilterIcon}
                    alt="Filter"
                    style={{ width: '56px', height: '56px' }}
                  />
                </Box>
                <Modal
                  open={showFilter}
                  onClose={() => setShowFilter(false)}
                  BackdropProps={{ invisible: true }} // This removes the black mask
                  sx={{
                    position: 'absolute',
                    top: { xs: '280px', md: '220px' }, // Adjust top positioning for xs and md
                    left: { xs: '50%', sm: 'auto' }, // Center on xs by positioning from the left at 50%
                    right: { xs: 'auto', sm: '40px' }, // Align to the right from sm onwards
                    transform: { xs: 'translateX(-50%)', sm: 'none' }, // Center horizontally on xs
                    width: { xs: 'auto', sm: 'auto' }, // Full width minus padding on xs, auto on sm and larger
                  }}
                >
                  <FilterComponent onFilterChange={handleFilterChange} />
                </Modal>
              </Box>
            </Box>
            <Box sx={{ color: 'white' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '20px',
                }}
              >
                <StyledButton
                  onClick={() => {
                    setShowConfirmationPopup(true);
                  }}
                >
                  Request Check-in
                </StyledButton>
              </div>
            </Box>
          </Box>
        </Box>
      </Box>

      {showConfirmationPopup && (
        <Popup
          icon={alert_icon}
          title=<Typography
            variant="h6"
            sx={{
              fontFamily: 'opensans',
              fontWeight: '700',
              lineHeight: '1.4',
            }}
          >
            <p>
              Are You Sure You <br /> Want to Request Check-in?
            </p>
          </Typography>
          buttonText="No"
          secondaryButtonText="Yes"
          onButtonClick={handleClickNo}
          onSecondaryButtonClick={handleClickYes}
        />
      )}

      {showPopup && (
        <Popup
          icon={!requestCheck && PopupClose}
          title=<Typography
            variant="h6"
            sx={{
              fontFamily: 'opensans',
              fontWeight: '700',
              lineHeight: '1.4',
            }}
          >
            {' '}
            <p style={{ padding: '0 5px' }}>{popupMessage}</p>
          </Typography>
          buttonText="Done"
          onButtonClick={handlePopup}
        />
      )}
    </>
  );
};

export default ConsumerCheckInCounts;
